import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import httpClient from './httpClient';
import { refreshCsrfToken } from './csrfService';

const RETRY_DELAYS_MS = [300, 1000, 2000];
const RETRYABLE_STATUS_CODES = new Set([502, 503, 504]);

export type FetchLikeResponse = {
  ok: boolean;
  status: number;
  statusText: string;
  headers: {
    get: (name: string) => string | null | undefined;
  };
  json: () => Promise<any>;
  text: () => Promise<string>;
};

const toHeadersRecord = (headers: Headers): Record<string, string> => {
  const record: Record<string, string> = {};
  headers.forEach((value, key) => {
    record[key] = value;
  });
  return record;
};

const buildFetchLikeResponse = (response: AxiosResponse): FetchLikeResponse => {
  const headerGetter = (name: string) => {
    const headerName = name.toLowerCase();
    const value = response.headers?.[headerName];
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value ?? null;
  };

  return {
    ok: response.status >= 200 && response.status < 300,
    status: response.status,
    statusText: response.statusText ?? '',
    headers: {
      get: headerGetter,
    },
    json: async () => response.data,
    text: async () => {
      const data = response.data;
      if (typeof data === 'string') {
        return data;
      }
      if (data === undefined || data === null) {
        return '';
      }
      try {
        return JSON.stringify(data);
      } catch (e) {
        return String(data);
      }
    },
  };
};

const normalizeUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') {
    return input;
  }
  if (input instanceof URL) {
    return input.pathname + input.search + input.hash;
  }
  if (typeof input === 'object' && 'url' in input) {
    return (input as Request).url;
  }
  return String(input);
};

const sleep = (delayMs: number): Promise<void> =>
  new Promise(resolve => {
    window.setTimeout(resolve, delayMs);
  });

const hasRetriesRemaining = (attempt: number): boolean => attempt < RETRY_DELAYS_MS.length;

const isRetryableResponseStatus = (status: number): boolean => RETRYABLE_STATUS_CODES.has(status);

const isRetryableNetworkError = (error: unknown): boolean =>
  axios.isAxiosError(error) && !error.response && error.code !== 'ERR_CANCELED';

export const authFetch = async (
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<FetchLikeResponse> => {
  const method = (init.method || 'GET').toString().toUpperCase();
  // Writes may have committed before a gateway/network failure is observed.
  const canRetry = method === 'GET' || method === 'HEAD';
  const headers = new Headers(init.headers || {});
  const url = normalizeUrl(input);

  const config: AxiosRequestConfig = {
    url,
    method,
    headers: toHeadersRecord(headers),
  };

  if (init.body !== undefined && method !== 'GET' && method !== 'HEAD') {
    config.data = init.body;
  }

  let recoveredCsrf = false;
  for (let attempt = 0; ; attempt++) {
    let response: AxiosResponse;
    try {
      response = await httpClient.request(config);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        response = error.response;
      } else {
        if (canRetry && hasRetriesRemaining(attempt) && isRetryableNetworkError(error)) {
          await sleep(RETRY_DELAYS_MS[attempt]);
          continue;
        }
        throw error;
      }
    }

    // Go emits this error before entering a CSRF-protected mutation handler.
    // Do not replay writes for arbitrary 403s or ambiguous transport failures.
    if (!canRetry && !recoveredCsrf && response.status === 403 &&
        response.data?.status === 403 && response.data?.message === 'Invalid CSRF token.') {
      recoveredCsrf = true;
      const token = await refreshCsrfToken();
      headers.set('X-XSRF-TOKEN', token);
      config.headers = toHeadersRecord(headers);
      continue;
    }
    if (canRetry && hasRetriesRemaining(attempt) && isRetryableResponseStatus(response.status)) {
      await sleep(RETRY_DELAYS_MS[attempt]);
      continue;
    }
    return buildFetchLikeResponse(response);
  }

};

// Utility to handle fetch responses
export const handleResponse = async (response: FetchLikeResponse) => {
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || errorBody.error || errorMessage;
    } catch (e) {
      // Ignore if response body is not JSON or empty
    }
    throw new Error(errorMessage);
  }
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.indexOf('application/json') !== -1) {
    return response.json();
  }
  return null;
};
